export default function creditForm(config = {}) {
  const locale = document.documentElement.lang || 'ro'
  const baseUrl = locale !== 'ro' ? `/${locale}` : ''

  return {
    // Mode can be 'page' (form page) or 'product' (product page)
    mode: config.mode || 'page',
    // Fixed product name when in product mode
    fixedProduct: config.fixedProduct || '',
    // Products list for the dropdown (form page only)
    products: window.__PRODUCTS__ || {},
    selected: null,
    isSubmitting: false,
    isDialogOpen: {
      successDialog: false,
      errorDialog: false
    },
    errors: [],
    serverErrors: {},
    data: {
      product: '',
      amount: '',
      period: '',
      // idnp: '',
      phone: '',
      privacy: false,
      credit_history: false,
    },

    init() {
      const store = this.$store.creditApplicationDraft

      // Determine if we should prefill from store
      // - On product page (mode='product'): always prefill from store (calculator is on same page)
      // - On form page (mode='page'): only prefill if ?prefill=1 is in URL (came from calculator)
      const urlParams = new URLSearchParams(window.location.search)
      const shouldPrefill = this.mode === 'product' || urlParams.get('prefill') === '1'

      // On form page without prefill flag, clear any stored data
      if (store && this.mode === 'page' && !shouldPrefill) {
        store.reset()
      }

      if (store && shouldPrefill) {
        // For product mode, use fixed product name
        if (this.mode === 'product' && this.fixedProduct) {
          this.data.product = this.fixedProduct
          // Try to find product by name for product mode
          const productByName = Object.values(this.products).find(p => p.title === this.fixedProduct)
          if (productByName) {
            this.selected = productByName
          }
        } else {
          // Try to find product by ID first (from calculator)
          if (store.product_id && this.products[store.product_id]) {
            this.selected = this.products[store.product_id]
            this.data.product = this.selected.title || store.product || ''
          } else if (store.product) {
            // Fallback to name-based lookup for backward compatibility
            const productByName = Object.values(this.products).find(p => p.title === store.product)
            if (productByName) {
              this.selected = productByName
              this.data.product = productByName.title || store.product
            } else {
              this.data.product = store.product
            }
          }
        }

        this.data.amount = store.amount || ''
        this.data.period = store.period || ''
        // this.data.idnp = store.idnp || ''
        this.data.phone = store.phone || ''
        this.data.privacy = store.privacy || false
        this.data.credit_history = store.credit_history || false
      }

      // On product page, watch for store changes from calculator
      if (store && this.mode === 'product') {
        this.$watch('$store.creditApplicationDraft.amount', (value) => {
          if (value && value !== this.data.amount) {
            this.data.amount = value
          }
        })
        this.$watch('$store.creditApplicationDraft.period', (value) => {
          if (value && value !== this.data.period) {
            this.data.period = value
          }
        })
      }

      // Watch product changes for form mode
      if (this.mode === 'page') {
        this.$watch('data.product', (value, oldValue) => {
          if (value !== oldValue) {
            this.setSelectedByProduct(value)
          }
        })
      }
      
      // Watch for product selection changes to update selected
      this.$watch('data.product', (value) => {
        if (value && !this.selected) {
          // Try to find product by title
          const product = Object.values(this.products).find(p => p.title === value)
          if (product) {
            this.selected = product
          }
        } else if (value && this.selected && this.selected.title !== value) {
          // Product changed, update selected
          const product = Object.values(this.products).find(p => p.title === value)
          if (product) {
            this.selected = product
          }
        }
      })

      // On product page, sync amount/period changes to store (for calculator sync)
      if (store && this.mode === 'product') {
        this.$watch('data.amount', (value) => {
          if (value !== store.amount) {
            store.amount = value
          }
        })
        this.$watch('data.period', (value) => {
          if (value !== store.period) {
            store.period = value
          }
        })
      }

      // Clear field errors when user starts typing
      // const fields = ['product', 'amount', 'period', 'idnp', 'phone', 'privacy', 'credit_history']
      const fields = ['product', 'amount', 'period', 'phone', 'privacy', 'credit_history']
      fields.forEach(field => {
        this.$watch(`data.${field}`, () => {
          this.clearFieldError(field)
        })
      })
    },

    openDialog(dialog) {
      this.isDialogOpen[dialog] = true
    },
    closeDialog(dialog) {
      this.isDialogOpen[dialog] = false
    },
    toggleDialog(dialog) {
      this.isDialogOpen[dialog] = !this.isDialogOpen[dialog]
    },

    get isSubmitBtnDisabled() {
      return this.isSubmitting || !this.isValid
    },

    get isValid() {
      // Basic required field validation
      if (!this.data.product) return false
      if (!this.data.amount) return false
      if (!this.data.period) return false
      // if (!this.data.idnp) return false
      if (!this.data.phone) return false
      if (!this.data.privacy) return false
      if (!this.data.credit_history) return false
      return true
    },

    setSelectedByProduct(product, { resetFields = true } = {}) {
      // product can be either ID (number) or title (string)
      if (typeof product === 'number' || (typeof product === 'string' && /^\d+$/.test(product))) {
        // It's an ID
        this.selected = this.products[parseInt(product)] || null
      } else {
        // It's a title - find by title
        this.selected = Object.values(this.products).find(p => p.title === product) || null
      }
      
      if (this.selected) {
        this.data.product = this.selected.title || product
      } else {
        this.data.product = product
      }
      
      if (resetFields) {
        this.data.amount = ''
        this.data.period = ''
      }
    },

    getFieldError(field) {
      return this.serverErrors[field] ? this.serverErrors[field][0] : null
    },

    hasError(field) {
      return this.errors.includes(field) || !!this.serverErrors[field]
    },

    clearFieldError(field) {
      this.errors = this.errors.filter(e => e !== field)
      delete this.serverErrors[field]
    },

    enforceAmountBounds() {
      if (!this.selected || !this.data.amount) return
      const num = parseInt(this.data.amount) || 0
      if (num < this.selected.min_price) {
        this.data.amount = String(this.selected.min_price)
      } else if (num > this.selected.max_price) {
        this.data.amount = String(this.selected.max_price)
      }
    },

    enforcePeriodBounds() {
      if (!this.selected || !this.data.period) return
      const num = parseInt(this.data.period) || 0
      if (num < this.selected.min_term) {
        this.data.period = String(this.selected.min_term)
      } else if (num > this.selected.max_term) {
        this.data.period = String(this.selected.max_term)
      }
    },

    async handleSubmit() {
      this.errors = []
      this.serverErrors = {}

      if (!this.isValid) {
        // Mark empty required fields as errors
        if (!this.data.product) this.errors.push('product')
        if (!this.data.amount) this.errors.push('amount')
        if (!this.data.period) this.errors.push('period')
        // if (!this.data.idnp) this.errors.push('idnp')
        if (!this.data.phone) this.errors.push('phone')
        if (!this.data.privacy) this.errors.push('privacy')
        if (!this.data.credit_history) this.errors.push('credit_history')
        return
      }

      this.isSubmitting = true

      try {
        const store = this.$store.creditApplicationDraft
        const payload = {
          product: this.data.product,
          amount: this.data.amount.toString().replace(/\s/g, ''),
          period: this.data.period.toString(),
          // idnp: this.data.idnp.replace(/\s/g, ''),
          phone: this.data.phone.replace(/\s/g, ''),
          privacy: this.data.privacy,
          credit_history: this.data.credit_history
        }

        // Add calculated amounts and percentages if they exist
        if (store) {
          if (store.monthly_payment !== null && store.monthly_payment !== undefined) {
            payload.monthly_payment = store.monthly_payment
          }
          if (store.total_payment !== null && store.total_payment !== undefined) {
            payload.total_payment = store.total_payment
          }
          if (store.total_interest !== null && store.total_interest !== undefined) {
            payload.total_interest = store.total_interest
          }
          if (store.interest_rate !== null && store.interest_rate !== undefined && store.interest_rate !== '') {
            payload.interest_rate = store.interest_rate
          } else if (this.selected?.interest_rate !== null && this.selected?.interest_rate !== undefined) {
            payload.interest_rate = this.selected.interest_rate
          }
          if (store.administration_fee !== null && store.administration_fee !== undefined) {
            payload.administration_fee = store.administration_fee
          }
          if (store.issuance_fee !== null && store.issuance_fee !== undefined) {
            payload.issuance_fee = store.issuance_fee
          }
          if (store.dae !== null && store.dae !== undefined) {
            payload.dae = store.dae
          }
          if (store.financial_item_id !== null && store.financial_item_id !== undefined) {
            payload.financial_item_id = store.financial_item_id
          }
          if (store.credit_history_item_id !== null && store.credit_history_item_id !== undefined) {
            payload.credit_history_item_id = store.credit_history_item_id
          }
        }

        await axios.post(`${baseUrl}/credit/apply`, payload, {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        })

        // Success - show dialog and reset
        this.openDialog('successDialog')

        // Keep calculator values on a product page so the form can be submitted
        // again without forcing the user to change the calculator first.
        if (this.$store.creditApplicationDraft) {
          if (this.mode === 'product') {
            this.$store.creditApplicationDraft.setPersonalData({
              idnp: '',
              phone: '',
              privacy: false,
              credit_history: false
            })
          } else {
            this.$store.creditApplicationDraft.reset()
          }
        }

        this.resetForm()
      } catch (e) {
        if (e.response?.status === 422) {
          // Validation errors from server
          this.serverErrors = e.response.data.errors || {}
          this.errors = Object.keys(this.serverErrors)
        } else {
          // General error
          this.openDialog('errorDialog')
        }
      } finally {
        this.isSubmitting = false
      }
    },

    resetForm() {
      const keepCalculatorData = this.mode === 'product'
      const amount = keepCalculatorData ? this.data.amount : ''
      const period = keepCalculatorData ? this.data.period : ''

      if (!keepCalculatorData) {
        this.selected = null
      }

      this.data = {
        product: keepCalculatorData ? this.fixedProduct : '',
        amount,
        period,
        // idnp: '',
        phone: '',
        privacy: false,
        credit_history: false,
      }
      this.errors = []
      this.serverErrors = {}
    },
  }
}
